import { FileNameTypes, useDeleteFilesMutation } from '@/__generated__/graphql';
import { Image } from '@/components/Analysis/shared/Image';
import * as state from '@/components/Analysis/state';
import { AttachmentMenu } from '@/components/shared/AttachmentDisplay/AttachmentMenu';
import { SideDrawer } from '@/components/shared/SideDrawer';
import { LocationTag } from '@/constants';
import { useDrawerWidths } from '@/hooks/useDrawerWidths';
import { DrawersProps } from '@/types';
import { downloadFile } from '@/utils';
import { getCloudfrontUrl } from '@/utils/cloudfront';
import { ChevronLeft, ChevronRight, Close } from '@mui/icons-material';
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { useAtom, useSetAtom } from 'jotai';
import { useMemo, useRef } from 'react';
import Slider from 'react-slick';
import {
  getThumbnailStyles,
  MainImageContainer,
  NavigationButton,
  SliderContainer,
} from './styles';

export const SplitViewPanel = ({ allClosed, isOpen, handleOpen }: DrawersProps) => {
  const [selectedIsometricFileId, setSelectedIsometricFileId] = useAtom(
    state.selectedIsometricFileId
  );
  const [assemblyDetails, setAssemblyDetails] = useAtom(state.selectedAssemblyDetails);
  const setSideBySideModeEnabled = useSetAtom(state.sideBySideModeEnabled);
  const [deleteFiles] = useDeleteFilesMutation();
  const { rightDrawerWidth } = useDrawerWidths();

  const referenceDrawingFiles = useMemo(() => {
    return (assemblyDetails?.files ?? [])?.filter(
      (file) => file.name === FileNameTypes.ReferenceDrawing || file.name === FileNameTypes.IsometricDrawing
    );
  }, [assemblyDetails]);

  const selectedReferenceDrawingFile = useMemo(() => {
    return referenceDrawingFiles.find((file) => file.id === selectedIsometricFileId);
  }, [referenceDrawingFiles, selectedIsometricFileId]);

  const handleClose = () => {
    setSelectedIsometricFileId(undefined);
    setSideBySideModeEnabled(false);
  };

  const sliderRef = useRef<Slider>(null);

  const sliderSettings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    centerMode: true,
    centerPadding: '0px',
    arrows: false,
    className: 'center-slide-content',
    variableWidth: false,
    adaptiveHeight: false,
    swipeToSlide: true,
    focusOnSelect: true,
    afterChange: (currentSlide: number) => {
      if (referenceDrawingFiles[currentSlide]) {
        setSelectedIsometricFileId(referenceDrawingFiles[currentSlide]?.id ?? '');
      }
    },
  };

  const handlePrev = () => {
    if (sliderRef.current) {
      sliderRef.current.slickPrev();
    }
  };

  const handleNext = () => {
    if (sliderRef.current) {
      sliderRef.current.slickNext();
    }
  };

  const handleDownloadAttachment = (attachmentIndex: number) => {
    const currentAttachment = referenceDrawingFiles[attachmentIndex];
    if (!currentAttachment.resourcePath) return;
    downloadFile(getCloudfrontUrl(currentAttachment.resourcePath), currentAttachment.fileName);
  };

  const handleRemoveFile = (fileIndex: number) => {
    const currentFile = referenceDrawingFiles[fileIndex];
    if (!currentFile.id) return;
    deleteFiles({
      variables: {
        input: {
          referenceId: assemblyDetails?.id ?? '',
          collectionName: 'assembly',
          fileIds: [currentFile.id],
        },
      },
    });
    setAssemblyDetails((previous) => {
      if (!previous) return;
      return {
        ...previous,
        files: previous.files?.filter((file) => file.id !== currentFile.id),
      };
    });
    if (referenceDrawingFiles.length === 1) {
      setSelectedIsometricFileId(undefined);
      setSideBySideModeEnabled(false);
    } else {
      let nextFileId = '';

      if (fileIndex === referenceDrawingFiles.length - 1) {
        const prevFile = referenceDrawingFiles[fileIndex - 1];
        nextFileId = prevFile?.id ?? '';
      } else {
        const nextFile = referenceDrawingFiles[fileIndex + 1];
        nextFileId = nextFile?.id ?? '';
      }
      setSelectedIsometricFileId(nextFileId);
    }
  };
  if (!assemblyDetails || !selectedIsometricFileId) return <></>;

  return (
    <SideDrawer
      allDrawersClosed={allClosed}
      position="right"
      width={rightDrawerWidth}
      isOpen={isOpen}
      handleOpen={handleOpen}
      iconStyles={{ top: 10, zIndex: '102 !important' }}
      sxPaper={{
        overflowY: 'hidden',
        border: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack
        padding="2.4rem"
        gap="1.6rem"
        id={LocationTag.ViewerSBSIsometricsPanel}
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
          <Typography variant="h6" fontWeight={700} lineHeight="1.6rem">
            {selectedReferenceDrawingFile?.caption ?? selectedReferenceDrawingFile?.fileName ?? ''}
          </Typography>
          <IconButton
            disableRipple
            sx={{ padding: 0 }}
            onClick={handleClose}
            data-testid="close-button"
            id={`${LocationTag.ViewerSBSIsometricsPanel}-close-button`}
          >
            <Close color="primary" />
          </IconButton>
        </Stack>
        {selectedReferenceDrawingFile && (
          <MainImageContainer className={`${LocationTag.ViewerSBSIsometricsPanel}-main-image`}>
            <Image
              src={getCloudfrontUrl(selectedReferenceDrawingFile?.resourcePath)}
              alt={selectedReferenceDrawingFile?.name}
              maxHeight="100%"
              maxWidth="100%"
              sx={{ objectFit: 'contain' }}
            />
          </MainImageContainer>
        )}
        <SliderContainer>
          <Box sx={{ width: '60%', margin: '0 auto', position: 'relative', overflow: 'visible' }}>
            <NavigationButton
              onClick={handlePrev}
              className="prev-button"
              data-testid="prev-button"
              disableRipple
            >
              <ChevronLeft sx={{ fontSize: '2.4rem' }} />
            </NavigationButton>

            <Slider ref={sliderRef} {...sliderSettings}>
              {referenceDrawingFiles.map((file) => (
                <Stack>
                  <Stack
                    direction="column"
                    alignItems="center"
                    justifyContent="center"
                    gap="0.4rem"
                    width="100%"
                    data-testid={`thumbnail-container-${file.id}`}
                    position="relative"
                    className={`${LocationTag.ViewerSBSIsometricsPanel}-thumbnail`}
                    key={file.id}
                    sx={{ margin: '0 auto', maxWidth: '100%', overflow: 'hidden' }}
                  >
                    <Image
                      src={getCloudfrontUrl(file?.resourcePath)}
                      alt={file?.name}
                      imgSx={{
                        height: '7.8rem',
                        width: '14.2rem',
                        margin: '0 auto',
                        display: 'block',
                        objectFit: 'cover',
                      }}
                      sx={{
                        ...getThumbnailStyles(selectedIsometricFileId, file),
                        display: 'flex',
                        justifyContent: 'center',
                        margin: '0 auto',
                      }}
                      onClick={() => setSelectedIsometricFileId(file?.id ?? '')}
                      data-testid={`thumbnail-${file.id}`}
                    />
                    <Tooltip arrow title={file?.caption ?? file?.fileName ?? ''} placement="bottom">
                      <Typography
                        variant="caption"
                        color="primary.400"
                        fontWeight={500}
                        maxWidth="9rem"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        whiteSpace="nowrap"
                        display="block"
                        textAlign="center"
                      >
                        {file?.caption ?? file?.fileName ?? ''}
                      </Typography>
                    </Tooltip>
                    <AttachmentMenu
                      canViewSideBySide={false}
                      handleSBSMode={() => {}}
                      handleRemoveAttachment={handleRemoveFile}
                      handleDownloadAttachment={handleDownloadAttachment}
                      index={referenceDrawingFiles.indexOf(file)}
                      location={LocationTag.ViewerSBSIsometricsPanel}
                    />
                  </Stack>
                </Stack>
              ))}
            </Slider>

            <NavigationButton
              onClick={handleNext}
              className="next-button"
              data-testid="next-button"
              disableRipple
            >
              <ChevronRight sx={{ fontSize: '2.4rem' }} />
            </NavigationButton>
          </Box>
        </SliderContainer>
      </Stack>
    </SideDrawer>
  );
};
